import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { SSOAuthResponse, SSOUserData, SSOProvider, SSOError } from '../interfaces/sso.interface';

@Injectable({
  providedIn: 'root',
})
export abstract class SSOBaseService {
  protected abstract provider: SSOProvider;
  protected abstract isInitialized: boolean;

  /**
   * Initialize the SSO provider
   */
  abstract initialize(config: any): Promise<boolean>;

  /**
   * Perform SSO authentication
   */
  abstract authenticate(): Observable<SSOAuthResponse>;

  /**
   * Check if the provider is available
   */
  abstract isAvailable(): boolean;

  /**
   * Get provider name
   */
  getProvider(): SSOProvider {
    return this.provider;
  }

  /**
   * Common error handling
   */
  protected handleError(error: any, context: string): Observable<SSOAuthResponse> {
    const ssoError: SSOError = {
      code: error.code || 'UNKNOWN_ERROR',
      message: error.message || 'An unknown error occurred',
      details: error,
    };

    console.error(`SSO Error (${this.provider} - ${context}):`, ssoError);

    return of({
      success: false,
      error: ssoError.message,
      errorCode: ssoError.code,
    });
  }

  /**
   * Validate user data
   */
  protected validateUserData(userData: Partial<SSOUserData>): boolean {
    return !!(userData.id && (userData.email || userData.displayName));
  }

  /**
   * Create standardized user data
   */
  protected createUserData(
    id: string,
    email?: string,
    firstName?: string,
    lastName?: string,
    displayName?: string,
    profilePicture?: string
  ): SSOUserData {
    return {
      id,
      email,
      firstName,
      lastName,
      displayName: displayName || `${firstName || ''} ${lastName || ''}`.trim(),
      profilePicture,
      provider: this.provider,
    };
  }

  /**
   * Log SSO events for debugging
   */
  protected logEvent(event: string, data?: any): void {
    console.log(`SSO Event (${this.provider}): ${event}`, data);
  }

  /**
   * Check if running in browser environment
   */
  protected isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  /**
   * Get current URL for redirect purposes
   */
  protected getCurrentUrl(): string {
    return this.isBrowser() ? window.location.origin : '';
  }
}
